export interface WebDocumentTitleOverride {
  readonly title: string;
  readonly description?: string | undefined;
  /** Path that owned this title. A later route ignores it. */
  readonly pathname: string;
}

interface ClaimedWebDocumentTitle extends WebDocumentTitleOverride {
  readonly id: number;
}

let nextClaimId = 0;
let currentClaim: ClaimedWebDocumentTitle | null = null;
const listeners = new Set<() => void>();

function notifyWebDocumentTitle(): void {
  listeners.forEach((listener) => {
    listener();
  });
}

/** The focused screen claims the tab title. A later claim replaces an earlier one. */
export function claimWebDocumentTitle(override: WebDocumentTitleOverride): number {
  nextClaimId += 1;
  currentClaim = {
    id: nextClaimId,
    title: override.title,
    description: override.description,
    pathname: override.pathname,
  };
  notifyWebDocumentTitle();
  return nextClaimId;
}

/** Clears the title only when this claim is still the active one. */
export function releaseWebDocumentTitle(claimId: number): void {
  if (currentClaim?.id !== claimId) {
    return;
  }
  currentClaim = null;
  notifyWebDocumentTitle();
}

export function subscribeWebDocumentTitle(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getWebDocumentTitleOverride(): WebDocumentTitleOverride | null {
  return currentClaim;
}
